// SendReceiptModal — confirmation modal for the per-receipt send / resend
// action. Mirrors the legacy `covd_receipt.php` send modal (Email | SMS
// channel toggle with editable contact field) but routes the request
// through the new POST /receipts/:enc_id/send endpoint.
//
// The address/number entered here is forwarded as `to_email` / `to_phone`
// — a per-send override. The receipt's stored customer_email /
// customer_phone are not mutated; if the staff member just confirms the
// pre-filled value, the server effectively dispatches to the same address
// either way.
//
// Resend vs. send: the action label switches based on receipt.is_sent so
// the same modal handles both flows.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { useSendReceipt } from '../hooks/useReceiptMutations';
import { useReceiptDetail } from '../hooks/useReceiptDetail';
import styles from './SendReceiptModal.module.css';

export type SendChannel = 'email' | 'sms';

export interface SendReceiptModalProps {
  open: boolean;
  encId: string | null;
  /** Whether the receipt has been sent before — flips title/button copy. */
  isResend: boolean;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  onClose: () => void;
  /** Fires after a successful send so the parent can invalidate caches. */
  onSent?: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SendReceiptModal({
  open,
  encId,
  isResend,
  customerName,
  customerEmail,
  customerPhone,
  onClose,
  onSent,
}: SendReceiptModalProps): JSX.Element | null {
  const sendMutation = useSendReceipt();

  // Lazy-fetch the receipt detail when the parent could not supply
  // a contact (e.g. the list page, whose ReceiptSummary lacks
  // customer_email). The hook is gated on a non-empty encId, so we
  // pass through the encId only while the modal is open AND at least
  // one of the contact fields is missing.
  const needsLazyContact =
    open && encId !== null && (customerEmail === null || customerPhone === null);
  const lazyDetail = useReceiptDetail(needsLazyContact ? encId : '');
  const lazyEmail = lazyDetail.data?.customer_email ?? null;
  const lazyPhone = lazyDetail.data?.customer_phone ?? null;

  const effectiveEmail = customerEmail ?? lazyEmail;
  const effectivePhone = customerPhone ?? lazyPhone;

  const [channel, setChannel] = useState<SendChannel>('email');
  const [emailValue, setEmailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset local state every time the modal is reopened so stale input
  // from a prior receipt does not leak between rows on the list page.
  useEffect(() => {
    if (open) {
      setChannel('email');
      setEmailValue(effectiveEmail ?? '');
      setPhoneValue(effectivePhone ?? '');
      setErrorMsg(null);
    }
  }, [open, encId, effectiveEmail, effectivePhone]);

  if (!open) return null;

  const submitting = sendMutation.isPending;
  const title = isResend ? 'Resend Receipt' : 'Send Receipt';
  const confirmLabel = isResend ? 'Resend Receipt' : 'Send Receipt';

  const currentValue = channel === 'email' ? emailValue : phoneValue;
  const trimmed = currentValue.trim();
  const inputEmpty = trimmed.length === 0;
  const emailInvalid = channel === 'email' && !inputEmpty && !EMAIL_RE.test(trimmed);
  const canSubmit = !submitting && !inputEmpty && !emailInvalid && encId !== null;

  function handleSubmit() {
    if (!canSubmit || encId === null) return;
    setErrorMsg(null);
    sendMutation.mutate(
      {
        encId,
        via: channel,
        ...(channel === 'email' ? { toEmail: trimmed } : { toPhone: trimmed }),
      },
      {
        onSuccess: () => {
          onSent?.();
          onClose();
        },
        onError: (err) => {
          const message = axios.isAxiosError(err)
            ? (err.response?.data as { message?: string } | undefined)?.message ??
              'Could not send the receipt. Please try again.'
            : 'Could not send the receipt. Please try again.';
          setErrorMsg(message);
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={title}
      size="sm"
      preventClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <p className={styles.subtitle}>
          {isResend
            ? 'Re-deliver this receipt to the customer.'
            : 'Send this receipt to the customer.'}
          {customerName ? (
            <>
              {' '}
              Recipient: <strong>{customerName}</strong>.
            </>
          ) : null}
        </p>

        <div
          className={styles.channelRow}
          role="radiogroup"
          aria-label="Send channel"
        >
          <button
            type="button"
            role="radio"
            aria-checked={channel === 'email'}
            onClick={() => setChannel('email')}
            className={
              channel === 'email' ? styles.channelActive : styles.channelOption
            }
            disabled={submitting}
          >
            Email
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={channel === 'sms'}
            onClick={() => setChannel('sms')}
            className={
              channel === 'sms' ? styles.channelActive : styles.channelOption
            }
            disabled={submitting}
          >
            SMS
          </button>
        </div>

        {channel === 'email' ? (
          <Input
            label="Send to"
            variant="email"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="customer@example.com"
            disabled={submitting}
            aria-label="Email address"
            error={
              emailInvalid
                ? { message: 'Enter a valid email address.' }
                : undefined
            }
          />
        ) : (
          <Input
            label="Send to"
            variant="tel"
            value={phoneValue}
            onChange={(e) => setPhoneValue(e.target.value)}
            placeholder="07000 000000"
            disabled={submitting}
            aria-label="Phone number"
          />
        )}

        {errorMsg !== null ? (
          <p role="alert" className={styles.error}>
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
